import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ExternalLink, Users, ChevronRight, X, Search,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { allProjects, categories } from '../data/projects';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function ProjectModal({ project, onClose }) {
  if (!project) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 30, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.92, y: 30, opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#0C1628] border border-white/10 rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto"
        >
          <div className="relative h-60">
            <img src={project.image} alt={project.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <button onClick={onClose}
              className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <span className="absolute bottom-4 left-5 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-lg">
              {project.cat}
            </span>
          </div>
          <div className="p-7">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 className="text-2xl font-bold text-white">{project.name}</h2>
              {project.url !== '#' && (
                <a href={project.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-sm font-semibold flex-shrink-0 mt-1">
                  <ExternalLink className="w-4 h-4" /> Visit
                </a>
              )}
            </div>
            <p className="text-slate-300 leading-relaxed mb-6">{project.fullDesc}</p>
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
              <Users className="w-4 h-4 text-orange-400" />
              <span className="font-medium">{project.scale}</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tech Stack</p>
              <div className="flex flex-wrap gap-2">
                {project.tech.map((t) => (
                  <span key={t} className="bg-white/8 border border-white/10 text-slate-300 text-sm px-3 py-1.5 rounded-lg font-medium">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = allProjects.filter((p) => {
    const matchCat = activeCategory === 'All' || p.cat === activeCategory;
    const matchSearch = search === '' ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.tech.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-10 right-20 w-80 h-80 bg-orange-500/15 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 left-20 w-60 h-60 bg-blue-600/15 rounded-full blur-[60px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.span variants={fadeUp}
              className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              {allProjects.length} Projects · 3+ Years · Real Clients
            </motion.span>
            <motion.h1 variants={fadeUp} className="text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Our <span className="text-orange-500">Portfolio</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-2xl mx-auto">
              From EdTech platforms to enterprise CRMs — every project we've shipped, start to finish.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FILTERS + SEARCH */}
      <section className="bg-[#060D1F] pt-10 pb-6 border-b border-white/8 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-500/20'
                    : 'bg-white/8 text-slate-300 hover:bg-orange-500/15 hover:text-orange-400'
                }`}>
                {cat}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search projects or tech..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            />
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-14 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-slate-400 text-lg">No projects match your search.</p>
              <button onClick={() => { setSearch(''); setActiveCategory('All'); }}
                className="mt-4 text-orange-400 font-semibold hover:underline">Clear filters</button>
            </div>
          ) : (
            <motion.div
              key={activeCategory + search}
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filtered.map((proj) => (
                <motion.div
                  key={proj.name}
                  variants={fadeUp}
                  whileHover={{ y: -6, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}
                  onClick={() => setSelected(proj)}
                  className="group bg-[#0C1628] border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:border-orange-500/40 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={proj.image} alt={proj.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <span className="absolute top-3 right-3 bg-orange-500/90 text-white text-xs font-bold px-2.5 py-1 rounded-lg">
                      {proj.cat}
                    </span>
                    <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1">
                        View Details <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-white font-bold text-lg mb-1.5 group-hover:text-orange-400 transition-colors">{proj.name}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4 line-clamp-2">{proj.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {proj.tech.slice(0, 3).map((t) => (
                        <span key={t} className="bg-white/8 text-slate-300 text-xs px-2.5 py-1 rounded-md font-medium">{t}</span>
                      ))}
                      {proj.tech.length > 3 && (
                        <span className="bg-white/8 text-slate-400 text-xs px-2.5 py-1 rounded-md">+{proj.tech.length - 3}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-orange-400 text-xs font-medium">
                      <Users className="w-3.5 h-3.5" /> {proj.scale}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          <p className="text-center text-slate-500 text-sm mt-10">
            Showing {filtered.length} of {allProjects.length} projects
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#080F1E] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mb-4">
              Have a project in mind?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mb-8">
              Let's build something great together. Reach out and we'll get back to you within 24 hours.
            </motion.p>
            <motion.button variants={fadeUp} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors">
              Start a Project <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />

      {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
